// Iter86b — Admin · Logistics Vehicle Types (Autótípus master list).
//
// CRUD UI for the EN/DE/HU-localized vehicle-type catalogue surfaced as
// a grouped dropdown in LogisticsEditor. Globals (tenant_id=null) come
// from the backend seed; admins add/edit/remove on top.
import React, { useState } from "react";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { useVehicleTypes, useCreateVehicleType, usePatchVehicleType, useDeleteVehicleType } from "../api/logistics";

const TRANSPORT_MODES = ["road", "container", "sea", "air", "rail", "intermodal", "multimodal"];
const CATEGORIES = [
  "road_curtainside", "road_groupage", "road_temperature", "road_special",
  "container", "sea", "air", "rail", "combined",
];

const empty = { category: "road_curtainside", label_en: "", label_de: "", label_hu: "", transport_mode: "road" };

export default function AdminVehicleTypes() {
  const q = useVehicleTypes();
  const create = useCreateVehicleType();
  const patch = usePatchVehicleType();
  const del = useDeleteVehicleType();
  const [newRow, setNewRow] = useState(null);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(empty);
  const items = q.data?.items || [];
  const grouped = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  const saveNew = () => {
    if (!newRow.label_en || !newRow.label_de || !newRow.label_hu) return;
    create.mutate(newRow, { onSuccess: () => setNewRow(null) });
  };
  const savePatch = () => {
    patch.mutate({ id: editId, body: edit }, { onSuccess: () => setEditId(null) });
  };

  return (
    <div className="space-y-4" data-testid="admin-logistics-vehicle-types">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 22, color: "var(--text-primary)" }}>Vehicle Types</h1>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", letterSpacing: "1.5px", textTransform: "uppercase" }}>Autótípus · master list · EN / DE / HU</div>
        </div>
        <button type="button" onClick={() => setNewRow(empty)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded" style={btnPrimary} data-testid="admin-vtype-new-btn">
          <Plus size={13} /> ADD TYPE
        </button>
      </header>

      {newRow && (
        <div className="rounded-md p-3" style={card} data-testid="admin-vtype-new-form">
          <div className="grid grid-cols-5 gap-2 mb-2">
            <Select value={newRow.category} onChange={(v) => setNewRow((r) => ({ ...r, category: v }))} options={CATEGORIES} testid="admin-vtype-new-category" />
            <Input value={newRow.label_en} placeholder="Label (EN)" onChange={(v) => setNewRow((r) => ({ ...r, label_en: v }))} testid="admin-vtype-new-en" />
            <Input value={newRow.label_de} placeholder="Label (DE)" onChange={(v) => setNewRow((r) => ({ ...r, label_de: v }))} testid="admin-vtype-new-de" />
            <Input value={newRow.label_hu} placeholder="Label (HU)" onChange={(v) => setNewRow((r) => ({ ...r, label_hu: v }))} testid="admin-vtype-new-hu" />
            <Select value={newRow.transport_mode} onChange={(v) => setNewRow((r) => ({ ...r, transport_mode: v }))} options={TRANSPORT_MODES} testid="admin-vtype-new-mode" />
          </div>
          <div className="flex gap-2">
            <button onClick={saveNew} className="inline-flex items-center gap-1 px-3 py-1.5 rounded" style={btnPrimary} data-testid="admin-vtype-new-save"><Save size={12} /> SAVE</button>
            <button onClick={() => setNewRow(null)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded" style={btnGhost}><X size={12} /> CANCEL</button>
          </div>
        </div>
      )}

      {Object.keys(grouped).sort().map((cat) => (
        <div key={cat} className="rounded-md p-3" style={card}>
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "var(--text-primary)", marginBottom: 6 }}>{cat}</div>
          <div className="space-y-1">
            {grouped[cat].map((it) => (
              <div key={it.id} className="grid grid-cols-[1.2fr_1.2fr_1.2fr_0.7fr_auto] gap-2 items-center" data-testid={`admin-vtype-row-${it.id}`}>
                {editId === it.id ? (
                  <>
                    <Input value={edit.label_en} onChange={(v) => setEdit((r) => ({ ...r, label_en: v }))} testid={`admin-vtype-edit-en-${it.id}`} />
                    <Input value={edit.label_de} onChange={(v) => setEdit((r) => ({ ...r, label_de: v }))} testid={`admin-vtype-edit-de-${it.id}`} />
                    <Input value={edit.label_hu} onChange={(v) => setEdit((r) => ({ ...r, label_hu: v }))} testid={`admin-vtype-edit-hu-${it.id}`} />
                    <Select value={edit.transport_mode} onChange={(v) => setEdit((r) => ({ ...r, transport_mode: v }))} options={TRANSPORT_MODES} testid={`admin-vtype-edit-mode-${it.id}`} />
                    <div className="flex gap-1">
                      <button onClick={savePatch} className="px-2 py-1 rounded" style={btnPrimary} data-testid={`admin-vtype-save-${it.id}`}><Save size={11} /></button>
                      <button onClick={() => setEditId(null)} className="px-2 py-1 rounded" style={btnGhost}><X size={11} /></button>
                    </div>
                  </>
                ) : (
                  <>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-primary)" }}>{it.label_en}</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dim)" }}>{it.label_de}</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dim)" }}>{it.label_hu}</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", textTransform: "uppercase" }}>{it.transport_mode}</span>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditId(it.id); setEdit({ category: it.category, label_en: it.label_en, label_de: it.label_de, label_hu: it.label_hu, transport_mode: it.transport_mode }); }} className="px-2 py-1 rounded" style={btnGhost} data-testid={`admin-vtype-edit-${it.id}`}><Pencil size={11} /></button>
                      <button onClick={() => { if (window.confirm(`Delete ${it.label_en}?`)) del.mutate(it.id); }} className="px-2 py-1 rounded" style={{ ...btnGhost, color: "#b22a29", border: "1px solid #b22a29" }} data-testid={`admin-vtype-delete-${it.id}`}><Trash2 size={11} /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const Input = ({ value, onChange, placeholder, testid }) => (
  <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="px-2 py-1.5 rounded outline-none w-full" style={input} data-testid={testid} />
);
const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="px-2 py-1.5 rounded outline-none w-full" style={input} data-testid={testid}>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const card = { background: "var(--bg-surface)", border: "1px solid var(--border-strong)" };
const input = { background: "var(--bg-elevated)", border: "1px solid var(--border-strong)", color: "var(--text-primary)", fontFamily: "'DM Mono', monospace", fontSize: 11 };
const btnPrimary = { background: "#185FA5", border: "1px solid #0d3f78", color: "#ffffff", fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: "0.4px", cursor: "pointer" };
const btnGhost = { background: "transparent", border: "1px solid var(--border-strong)", color: "var(--text-dim)", fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 11, cursor: "pointer" };
